import { Auth0ContextInterface } from "@auth0/auth0-react";
import { makeOperation, Operation } from "@urql/core";
import { AuthConfig } from "@urql/exchange-auth/dist/types/authExchange";
import { datadogLogs } from "@datadog/browser-logs";
import { LoggerOptions } from "../../monitoring/LoggerOptions";
import { GraphQLError } from "graphql";
import { errorExchange } from "urql";
import { differenceInMilliseconds } from "date-fns";

const logger = datadogLogs.createLogger(
  "auth0AuthenticationExchange",
  LoggerOptions
);
type AuthDependencies = Pick<
  Auth0ContextInterface,
  "getAccessTokenSilently" | "logout" | "getIdTokenClaims"
>;
export function hasAuthErrors(graphQLErrors: GraphQLError[]): boolean {
  return graphQLErrors.some(({ extensions, message }) => {
    //We probably get the code, but if not fallback to the message.
    return (
      ["UNAUTHENTICATED", "FORBIDDEN"].includes(extensions?.code) ||
      message === "authenticated endpoints require an auth token" ||
      message === "jwt must be provided"
    );
  });
}

function isTokenAboutToExpire(expirationTime: number): boolean {
  const difference = differenceInMilliseconds(
    expirationTime * 1000,
    Date.now()
  );
  return difference < 50;
}
export type AuthState = {
  //The auth token, hold operations that require auth until this has a value.
  token: string | null;
  expirationTime?: number;
  //Did an error occur, if you reach an operation that requires authentication, it should not proceed
  //if our last attempt resulted in an error.
  error?: Error;
};
export function buildAuthExchangeOptions({
  getAccessTokenSilently,
  getIdTokenClaims
}: AuthDependencies): AuthConfig<AuthState> {
  return {
    addAuthToOperation({ authState, operation }): Operation {
      if (!authState || !authState.token) {
        return operation;
      }
      const fetchOptions: any =
        typeof operation.context.fetchOptions === "function"
          ? operation.context.fetchOptions()
          : operation.context.fetchOptions || {};
      const { token } = authState;
      return makeOperation(operation.kind, operation, {
        ...operation.context,
        fetchOptions: {
          ...fetchOptions,
          headers: {
            ...fetchOptions.headers,
            authorization: `Bearer ${token}`
          }
        }
      });
    },
    async getAuth({ authState }): Promise<AuthState | null> {
      //We could be racing the handleRedirectCallback internal to auth0, so don't bother
      try {
        const token = await getAccessTokenSilently({
          ignoreCache: authState?.expirationTime
            ? isTokenAboutToExpire(authState.expirationTime)
            : false
        }); // if the token is expired or is about to, ignore the cache and go straight to the auth endpoint for a new token
        const { exp } = await getIdTokenClaims();
        if (token) {
          if (!exp) {
            logger.warn(
              "No expiration for the current JWT, the session is treated as infinite duration."
            );
          }
          return {
            token,
            expirationTime: exp
          };
        }
      } catch (e) {
        //Refresh failed
        logger.error("getAccessTokenSilently", {
          error: e
        });
        return {
          token: null,
          error: e
        };
      }
      return authState;
    },
    didAuthError({ error }): boolean {
      const { graphQLErrors } = error;
      return hasAuthErrors(graphQLErrors);
    },
    willAuthError({ authState, operation }): boolean {
      const { context } = operation;
      const allowUnAuthenticated = !!context["allowUnAuthenticated"] || false;
      if (allowUnAuthenticated) return false;
      if (!authState) return true;
      const { token, error, expirationTime } = authState;
      if (!token) return true;
      if (error) return true;
      if (expirationTime) return isTokenAboutToExpire(expirationTime); // if time difference is less than 50 milliseconds, it will error
      return false; // if all checks pass, return false, it is treated as a token that cannot time out.
    }
  };
}

/**
 * The exchange sits higher in the stack, this allows us to capture and log errors that might results from
 * auth issues seperate from the logout, place this as high as possible up the chain.
 *
 * The auth exchange should stay focused on getting the auth token and managing operation state.
 *
 * It also gives a simple entry point to do something other than logout, like show a notification, and make the user hit
 * logout.
 * @param auth0
 */
export const logoutOnBubbledAuthError = (auth0: AuthDependencies) =>
  errorExchange({
    onError: (error, operation) => {
      if (hasAuthErrors(error.graphQLErrors)) {
        if (!operation.context?.noLogout) {
          console.log("Logging out due to authorization error from server");
          auth0.logout();
        }
      }
    }
  });
